import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Eye, Play, RefreshCw, Undo2, X } from "lucide-react";
import toast from "react-hot-toast";
import { formatInr } from "./runPayrollUtils";
import { displayLabel } from "../../../utils/displayLabel";
import { payrollOpsService, payrollPolicyService } from "../../../services/api";
import {
  exportAccountingJournalExcel,
  exportLeaveEncashmentAwayExcel,
  exportPayrollProjectionExcel,
  exportPayrollReconciliationExcel,
  exportPayrollRegisterExcel,
} from "./exportPayrollReportsExcel";
import { exportUnpaidSalaryExcel } from "./exportPaymentLedgerExcel";
import SalaryRegisterExportModal, {
  loadSalaryRegisterExportPrefs,
} from "./SalaryRegisterExportModal";
import PayrollReportsMenu, { PAYROLL_QUICK_REPORT_ITEMS } from "./PayrollReportsMenu";
import PayrollAllProcessedGateModal from "./PayrollAllProcessedGateModal";
import PayrollReconciliationExportModal from "./PayrollReconciliationExportModal";
import PayrollDeclarationsTdsPanel, {
  OverrideModal,
} from "./PayrollDeclarationsTdsPanel";
import { usePermissions } from "../../../rbac/PermissionsContext";
import EmpCodeNameSearchBar from "../../Common/EmpCodeNameSearchBar";
import { filterRowsByEmpCodeOrName } from "../../../lib/empCodeNameSearch";

const REPORT_LABELS = {
  projection: "Payroll Projection",
  register: "Payroll Register",
  reconciliation: "Payroll Reconciliation",
  journal: "Accounting Journal",
  unpaid: "Unpaid Salary",
  le_away: "Leave Encashment (Away)",
};

/** Count from stored breakdown netPay only — no recalculation. */
export function countNegativeNetFromRows(rows) {
  return (rows || []).filter(
    (r) =>
      r.status === "processed" &&
      r.breakdownJson &&
      Number(r.breakdownJson.netPay) < 0
  ).length;
}

const LOCK_VERB = { process: "processing", reprocess: "reprocessing", unprocess: "unprocessing" };

/** Inline status while the server is still working on this run (polled by the wizard). */
function ServerBusyCard({ busy, darkMode }) {
  const lock = busy.lock;
  const started = lock?.startedAt
    ? new Date(lock.startedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
    : null;
  const remaining = (busy.resumeIds || []).length;
  const title =
    busy.reason === "timeout"
      ? "Finishing the last batch on the server…"
      : `${lock?.userName || "Another user"} is ${LOCK_VERB[lock?.action] || "processing"} payroll${
          started ? ` (started ${started})` : ""
        }`;
  const detail =
    busy.reason === "timeout"
      ? `${busy.done} of ${busy.total} done.${
          remaining ? ` The remaining ${remaining} will continue automatically.` : ""
        }`
      : remaining
        ? `Your remaining ${remaining} will continue automatically when it finishes.`
        : "Actions unlock automatically when it finishes.";

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-xs ${
        darkMode ? "border-sky-800/50 bg-sky-950/30 text-sky-100" : "border-sky-200 bg-sky-50 text-sky-900"
      }`}
    >
      <RefreshCw size={16} className="mt-0.5 shrink-0 animate-spin" />
      <div>
        <p className="font-semibold">{title}</p>
        <p className="mt-0.5 opacity-80">{detail}</p>
      </div>
    </div>
  );
}

/** Post-process popup listing negative-net employees (stored netPay only). */
function NegativeNetPayModal({ open, rows, onClose, onShowInList, onViewBreakdown }) {
  const list = useMemo(
    () =>
      (rows || [])
        .filter(
          (r) => r.status === "processed" && r.breakdownJson && Number(r.breakdownJson.netPay) < 0
        )
        .map((r) => {
          const b = r.breakdownJson;
          return {
            row: r,
            empCode: b.empCode || r.empCode || r.employeeId,
            name: b.name || b.employeeName || r.name || "—",
            paidDays: b.paidDays,
            grossPay: b.grossPay,
            netPay: b.netPay,
          };
        })
        .sort((a, b) => Number(a.netPay) - Number(b.netPay)),
    [rows]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !list.length) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="negative-net-title"
        className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 shrink-0 text-[hsl(var(--ds-danger))]" size={18} />
            <div>
              <h3 id="negative-net-title" className="text-sm font-semibold">
                {list.length} employee{list.length === 1 ? "" : "s"} with negative net pay
              </h3>
              <p className="mt-0.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Deductions exceed earnings this month. Review before disbursement.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[hsl(var(--ds-muted))]">
              <tr>
                <th className="px-3 py-2 text-left font-bold">Code</th>
                <th className="px-3 py-2 text-left font-bold">Name</th>
                <th className="px-3 py-2 text-right font-bold">Paid days</th>
                <th className="px-3 py-2 text-right font-bold">Gross</th>
                <th className="px-3 py-2 text-right font-bold">Net</th>
                <th className="px-3 py-2 text-center font-bold">View</th>
              </tr>
            </thead>
            <tbody>
              {list.map((e) => (
                <tr key={e.row.id || e.empCode} className="border-t border-[hsl(var(--ds-border))]">
                  <td className="px-3 py-1.5 font-medium">{displayLabel(e.empCode)}</td>
                  <td className="px-3 py-1.5">{displayLabel(e.name)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{e.paidDays ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatInr(e.grossPay)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-[hsl(var(--ds-danger))]">
                    {formatInr(e.netPay)}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onViewBreakdown?.(e.row);
                      }}
                      className="inline-flex text-[hsl(var(--ds-primary))]"
                      title="Full breakdown"
                      aria-label={`View breakdown for ${e.empCode}`}
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <button
            type="button"
            onClick={() => {
              onClose();
              onShowInList();
            }}
            className="rounded border border-[hsl(var(--ds-border))] px-3 py-1.5 text-xs font-semibold hover:border-[hsl(var(--ds-primary))]"
          >
            Show in list
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white"
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function computeProcessGate(steps) {
  const POST = new Set([
    "disbursement",
    "checklist",
    "company_checklist",
    "preview_validate",
    "finalise",
    "payslips",
    "process",
  ]);
  const list = (steps || []).filter((s) => s.enabled !== false && !POST.has(s.key));
  const blocking = list.filter((s) => s.blocking);
  const failed = blocking.filter((s) => s.status === "failed");
  const pending = blocking.filter((s) => !["passed", "acknowledged"].includes(s.status));

  if (failed.length) {
    return { ok: false, message: failed.map((s) => s.label).join(", "), failedSteps: failed.map((s) => s.key) };
  }
  if (pending.length) {
    return { ok: false, message: pending.map((s) => s.label).join(", "), failedSteps: pending.map((s) => s.key) };
  }
  return { ok: true };
}

function ProgressRing({ done, total, label, color = "#059669", darkMode, subtitle = null }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const r = 34;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative h-24 w-24">
        <svg className="h-24 w-24 -rotate-90" viewBox="0 0 80 80" aria-hidden>
          <circle
            cx="40"
            cy="40"
            r={r}
            fill="none"
            stroke={darkMode ? "#333" : "#e5e7eb"}
            strokeWidth="8"
          />
          <circle
            cx="40"
            cy="40"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.25s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-sm font-semibold tabular-nums">
            {done}/{total}
          </span>
          <span
            className={`text-[9px] ${
              darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
            }`}
          >
            {pct}%
          </span>
        </div>
      </div>
      <p
        className={`text-[11px] font-medium ${
          darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"
        }`}
      >
        {label}
      </p>
      {subtitle ? (
        <p className="max-w-[220px] text-center text-[10px] text-[hsl(var(--ds-muted-fg))]">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

/** Single sequential progress: Phase 1 payroll → Phase 2 TDS (one ring). */
function ProcessPhaseProgress({ batchProgress, darkMode }) {
  if (!batchProgress) return null;
  const total = Number(batchProgress.total) || 0;
  const done = Number(batchProgress.done) || 0;
  const phase = batchProgress.phase === "tds" ? "tds" : "payroll";
  const isTds = phase === "tds";
  const label = isTds ? "TDS computation" : "Payroll calculation";
  const color = isTds ? "#2563eb" : "#059669";
  const step = isTds ? 2 : 1;

  return (
    <div
      className={`flex flex-col items-center gap-3 px-4 py-4 ${
        darkMode ? "" : ""
      }`}
    >
      <ProgressRing
        darkMode={darkMode}
        done={done}
        total={total}
        label={label}
        color={color}
        subtitle={`Step ${step} of 2 · ${
          isTds
            ? "Applying tax from processed payroll values"
            : "Earnings, statutory, variables"
        }`}
      />
      <div className="w-full max-w-md space-y-1.5">
        <div className="flex justify-between text-[10px] font-medium text-[hsl(var(--ds-muted-fg))]">
          <span className={!isTds ? "font-semibold text-emerald-700 dark:text-emerald-400" : ""}>
            1 · Payroll{!isTds ? "…" : " ✓"}
          </span>
          <span className={isTds ? "font-semibold text-blue-700 dark:text-blue-400" : ""}>
            2 · TDS{isTds ? "…" : ""}
          </span>
        </div>
        <div className="flex h-2 gap-1 overflow-hidden">
          <div
            className={`h-full flex-1 overflow-hidden rounded-full ${
              darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-emerald-100"
            }`}
          >
            <div
              className="h-full rounded-full bg-emerald-600 transition-all duration-300"
              style={{
                width: `${
                  total > 0
                    ? Math.min(100, Math.round(((isTds ? total : done) / total) * 100))
                    : 0
                }%`,
              }}
            />
          </div>
          <div
            className={`h-full flex-1 overflow-hidden rounded-full ${
              darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-blue-100"
            }`}
          >
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-300"
              style={{
                width: `${
                  isTds && total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
                }%`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TdsProcessSummaryCard({ tdsSummary, darkMode, onOpenTdsReview }) {
  const avgTds =
    tdsSummary.withTds > 0
      ? Math.round((tdsSummary.totalTds / tdsSummary.withTds) * 100) / 100
      : 0;
  const coveragePct =
    tdsSummary.processed > 0
      ? Math.round((tdsSummary.withTds / tdsSummary.processed) * 100)
      : 0;
  const matchPct =
    tdsSummary.processed > 0 && tdsSummary.delta === 0
      ? 100
      : tdsSummary.processed > 0
        ? Math.max(
            0,
            Math.round(
              ((tdsSummary.processed - tdsSummary.overrideCount) / tdsSummary.processed) * 100
            )
          )
        : 0;

  const insights = [];
  if (tdsSummary.processed > 0) {
    insights.push(
      `${coveragePct}% of processed employees have TDS this month (${tdsSummary.withTds}/${tdsSummary.processed}).`
    );
  }
  if (tdsSummary.withTds > 0) {
    insights.push(`Average TDS among those with tax: ${formatInr(avgTds)}.`);
  }
  if (tdsSummary.overrideCount > 0) {
    insights.push(
      `${tdsSummary.overrideCount} manual override${tdsSummary.overrideCount === 1 ? "" : "s"} — review those rows first.`
    );
  } else if (tdsSummary.delta === 0 && tdsSummary.processed > 0) {
    insights.push("Applied TDS matches computed for every employee — no override drift.");
  } else if (Math.abs(tdsSummary.delta) > 0) {
    insights.push(
      `Net applied vs computed variance: ${tdsSummary.delta > 0 ? "+" : ""}${formatInr(tdsSummary.delta)}.`
    );
  }
  if (tdsSummary.processed > 0 && tdsSummary.overrideCount === 0) {
    insights.push(`${matchPct}% rows use system-computed TDS (no overrides).`);
  }

  return (
    <aside
      className={`flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]`}
    >
      <div className="border-b border-[hsl(var(--ds-border))] px-3 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-fg))]">
          TDS details
        </p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
        <div>
          <p className="text-[10px] text-[hsl(var(--ds-muted-fg))]">Applied TDS</p>
          <p className="text-xl font-semibold tabular-nums tracking-tight text-[hsl(var(--ds-fg))]">
            {formatInr(tdsSummary.totalTds)}
          </p>
        </div>
        <dl className="space-y-2 text-[11px]">
          <div className="flex items-baseline justify-between gap-2 border-b border-[hsl(var(--ds-border))] pb-1.5">
            <dt className="text-[hsl(var(--ds-muted-fg))]">Computed</dt>
            <dd className="font-medium tabular-nums">{formatInr(tdsSummary.totalComputed)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-2 border-b border-[hsl(var(--ds-border))] pb-1.5">
            <dt className="text-[hsl(var(--ds-muted-fg))]">Δ (applied − computed)</dt>
            <dd className="font-medium tabular-nums">
              {tdsSummary.delta === 0
                ? "—"
                : `${tdsSummary.delta > 0 ? "+" : ""}${formatInr(tdsSummary.delta)}`}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-2 border-b border-[hsl(var(--ds-border))] pb-1.5">
            <dt className="text-[hsl(var(--ds-muted-fg))]">Overrides</dt>
            <dd className="font-medium tabular-nums">{tdsSummary.overrideCount}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-[hsl(var(--ds-muted-fg))]">Employees with TDS</dt>
            <dd className="font-medium tabular-nums">
              {tdsSummary.withTds}/{tdsSummary.processed}
            </dd>
          </div>
        </dl>
        {insights.length > 0 ? (
          <ul className="space-y-1.5 text-[10px] leading-relaxed text-[hsl(var(--ds-muted-fg))]">
            {insights.map((line) => (
              <li key={line} className="flex gap-1.5">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {typeof onOpenTdsReview === "function" ? (
          <button
            type="button"
            onClick={onOpenTdsReview}
            className={`mt-auto w-full rounded-md border px-2 py-1.5 text-[11px] font-semibold transition-colors ${
              darkMode
                ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted)/0.8)]"
                : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
            }`}
          >
            TDS Review / Audit
          </button>
        ) : null}
      </div>
    </aside>
  );
}

function compareSortValues(a, b, key) {
  const va = a?.[key];
  const vb = b?.[key];
  const na = Number(va);
  const nb = Number(vb);
  if (Number.isFinite(na) && Number.isFinite(nb) && String(va).trim() !== "" && String(vb).trim() !== "") {
    return na - nb;
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function UnprocessedOrFailedTable({ rows, darkMode, emptyLabel }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(
    () => filterRowsByEmpCodeOrName(rows || [], search),
    [rows, search]
  );

  if (!(rows || []).length) {
    return (
      <p
        className={`rounded-lg border border-dashed px-4 py-8 text-center text-sm ${
          darkMode
            ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
            : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
        }`}
      >
        {emptyLabel}
      </p>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-2 overflow-hidden p-2">
      <div className="shrink-0">
        <EmpCodeNameSearchBar
          value={search}
          onChange={setSearch}
          darkMode={darkMode}
          showCount
          total={rows.length}
          filtered={filtered.length}
          className="!flex-none"
        />
      </div>
      {!filtered.length ? (
        <p
          className={`rounded-lg border border-dashed px-4 py-8 text-center text-sm ${
            darkMode
              ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
              : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          No employees match “{search.trim()}”.
        </p>
      ) : (
        <div className="ds-table-scroll-fill text-xs">
          <table className="w-full min-w-[640px]">
            <thead
              className={`sticky top-0 z-10 ${
                darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"
              }`}
            >
              <tr>
                <th className="px-2 py-2 text-left font-bold">Sl. No</th>
                <th className="px-2 py-2 text-left font-bold">Code</th>
                <th className="px-2 py-2 text-left font-bold">Name</th>
                <th className="px-2 py-2 text-left font-bold">Reason</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr
                  key={row.id || row.employeeId || row.empCode || i}
                  className={
                    darkMode
                      ? "border-t border-[#3C3C3C]"
                      : "border-t border-[hsl(var(--ds-border))]"
                  }
                >
                  <td className="px-2 py-1.5 tabular-nums text-[hsl(var(--ds-muted-fg))]">
                    {i + 1}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.empCode || "—"}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.name || "—"}</td>
                  <td
                    className={`px-2 py-1.5 ${
                      row.errorMessage
                        ? "text-[hsl(var(--ds-danger))]"
                        : "text-[hsl(var(--ds-muted-fg))]"
                    }`}
                    title={row.errorMessage || ""}
                  >
                    {row.errorMessage || "Not processed yet"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProcessedResultsTable({
  rows,
  darkMode,
  onViewBreakdown,
  revealNegativeKey = 0,
  payMonth = "",
  finalized = false,
  canEditTds = false,
  onTdsSaved,
  hideToolbar = false,
  search = "",
  onSearchChange,
  showNegativeSalary = false,
  onShowNegativeChange,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [localSearch, setLocalSearch] = useState("");
  const [localNegative, setLocalNegative] = useState(false);
  const [tdsEditRow, setTdsEditRow] = useState(null);

  const searchValue = onSearchChange ? search : localSearch;
  const setSearch = onSearchChange || setLocalSearch;
  const negativeOn = onShowNegativeChange ? showNegativeSalary : localNegative;
  const setNegative = onShowNegativeChange || setLocalNegative;

  useEffect(() => {
    if (revealNegativeKey > 0) setNegative(true);
  }, [revealNegativeKey, setNegative]);

  const processed = useMemo(
    () =>
      (rows || [])
        .filter((r) => r.status === "processed" && r.breakdownJson)
        .map((r) => {
          const b = r.breakdownJson || {};
          return {
            ...r,
            empCode: b.empCode || r.empCode || r.employeeId,
            name: b.name || b.employeeName || r.name || "—",
            paidDays: b.paidDays,
            grossPay: b.grossPay,
            tdsAmount: Number(b.tdsAmount ?? b.tds ?? 0) || 0,
            netPay: b.netPay,
            isOverridden: !!(b.tdsMeta?.isOverridden || b.tdsMeta?.override),
            computedTds: Number(b.tdsMeta?.computedTds ?? b.tdsAmount ?? b.tds ?? 0) || 0,
            actualTds: Number(b.tdsAmount ?? b.tds ?? 0) || 0,
          };
        }),
    [rows]
  );

  const negativeNetCount = useMemo(
    () => processed.filter((r) => Number(r.netPay) < 0).length,
    [processed]
  );

  const filtered = useMemo(() => {
    let list = filterRowsByEmpCodeOrName(processed, searchValue);
    if (negativeOn) {
      list = list.filter((r) => Number(r.netPay) < 0);
    }
    return list;
  }, [processed, searchValue, negativeOn]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (!sortKey) return list;
    list.sort((a, b) => {
      const cmp = compareSortValues(a, b, sortKey);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const columns = [
    { key: "empCode", label: "Code" },
    { key: "name", label: "Name" },
    { key: "paidDays", label: "Paid days", align: "right" },
    { key: "grossPay", label: "Gross", format: "inr" },
    { key: "tdsAmount", label: "TDS", format: "inr" },
    { key: "netPay", label: "Net", format: "inr" },
  ];

  if (!processed.length) {
    return (
      <p
        className={`rounded-lg border border-dashed px-4 py-8 text-center text-sm ${
          darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
        }`}
      >
        No processed employees yet. Use Process to calculate payroll for this month.
      </p>
    );
  }

  const negativeLabel = (
    <label
      className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded border px-2 py-1 text-[11px] ${
        negativeOn || negativeNetCount > 0
          ? darkMode
            ? "border-red-800/60 bg-red-950/40 text-red-100"
            : "border-red-300 bg-red-50 text-red-900"
          : darkMode
            ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
            : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
      }`}
      title="Show only processed employees whose net pay is less than zero"
    >
      <input
        type="checkbox"
        className="h-3.5 w-3.5 accent-[hsl(var(--ds-danger))]"
        checked={!!negativeOn}
        onChange={(e) => setNegative(e.target.checked)}
      />
      <span>
        Show Negative Salary{" "}
        <strong className="tabular-nums">({negativeNetCount})</strong>
      </span>
    </label>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-2 overflow-hidden p-2">
      {!hideToolbar ? (
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 w-72 sm:w-80">
            <EmpCodeNameSearchBar
              value={searchValue}
              onChange={setSearch}
              darkMode={darkMode}
              className="!flex-none w-full"
            />
          </div>
          {negativeLabel}
        </div>
      ) : null}
      {!sorted.length ? (
        <p
          className={`rounded-lg border border-dashed px-4 py-8 text-center text-sm ${
            darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          {negativeOn && !String(searchValue || "").trim() && negativeNetCount === 0
            ? "No employees with negative net pay for this month."
            : negativeOn && String(searchValue || "").trim()
              ? `No negative-net employees match “${String(searchValue).trim()}”.`
              : `No employees match “${String(searchValue || "").trim()}”.`}
        </p>
      ) : (
    <div className="ds-table-scroll-fill text-xs">
      <table className="w-full min-w-[720px]">
        <thead className={`sticky top-0 z-10 ${darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}`}>
          <tr>
            <th className="px-2 py-2 text-left font-bold whitespace-nowrap">Sl. No</th>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`px-2 py-2 font-bold whitespace-nowrap ${
                  c.format === "inr" || c.align === "right" ? "text-right" : "text-left"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleSort(c.key)}
                  className={`font-bold hover:text-[hsl(var(--ds-primary))] ${
                    c.format === "inr" || c.align === "right" ? "w-full text-right" : "text-left"
                  } ${sortKey === c.key ? "text-[hsl(var(--ds-primary))]" : ""}`}
                  title={`Sort by ${c.label}`}
                >
                  {c.label}
                </button>
              </th>
            ))}
            <th className="px-2 py-2 text-center font-bold">View</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr
              key={row.id || row.employeeId || i}
              className={darkMode ? "border-t border-[#3C3C3C]" : "border-t border-[hsl(var(--ds-border))]"}
            >
              <td className="px-2 py-1.5 tabular-nums text-[hsl(var(--ds-muted-fg))]">{i + 1}</td>
              {columns.map((c) => {
                const isNegNet = c.key === "netPay" && Number(row.netPay) < 0;
                if (c.key === "tdsAmount" && canEditTds && !finalized) {
                  return (
                    <td key={c.key} className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setTdsEditRow(row)}
                        className={`font-semibold underline-offset-2 hover:underline ${
                          row.isOverridden
                            ? "text-amber-700 dark:text-amber-300"
                            : "text-indigo-700 dark:text-indigo-300"
                        }`}
                        title="Edit TDS for this processed payslip"
                      >
                        {formatInr(row.tdsAmount)}
                      </button>
                    </td>
                  );
                }
                return (
                  <td
                    key={c.key}
                    className={`px-2 py-1.5 tabular-nums whitespace-nowrap ${
                      c.format === "inr" || c.align === "right" ? "text-right" : "text-left"
                    } ${isNegNet ? "font-semibold text-[hsl(var(--ds-danger))]" : ""}`}
                  >
                    {c.format === "inr" ? formatInr(row[c.key]) : row[c.key] ?? "—"}
                  </td>
                );
              })}
              <td className="px-2 py-1.5 text-center">
                <button
                  type="button"
                  onClick={() => onViewBreakdown?.(row)}
                  className="inline-flex text-[hsl(var(--ds-primary))]"
                  title="Full breakdown"
                >
                  <Eye size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      )}
      {tdsEditRow ? (
        <OverrideModal
          row={tdsEditRow}
          payMonth={payMonth}
          darkMode={darkMode}
          locked={false}
          allowProcessed
          onClose={() => setTdsEditRow(null)}
          onSaved={() => onTdsSaved?.()}
        />
      ) : null}
    </div>
  );
}

export function ProcessPayrollActions({
  darkMode,
  run,
  steps = [],
  unprocessedCount = 0,
  processedCount = 0,
  stepBusy,
  serverBusy = false,
  onOpenAction,
  prevMonthBlocked = false,
  finalized = false,
  /** RBAC: pay_proc_process Edit — hide Process / Reprocess / Unprocess when false */
  canMutate = true,
}) {
  const gate = useMemo(() => computeProcessGate(steps), [steps]);
  const busyTitle = serverBusy ? "Payroll is being processed — actions unlock automatically when it finishes" : null;
  const hasAnyProcessed =
    ["processed", "reprocessed"].includes(run?.status) || processedCount > 0;
  const blocked = prevMonthBlocked || stepBusy || finalized;
  const canProcess = canMutate && unprocessedCount > 0 && gate.ok && !prevMonthBlocked && !finalized;
  const canReprocess = canMutate && processedCount > 0 && gate.ok && !prevMonthBlocked && !finalized;
  const canUnprocess = canMutate && processedCount > 0 && !prevMonthBlocked && !finalized;

  return (
    <>
      {!gate.ok && (
        <p className="max-w-[220px] truncate text-[10px] text-amber-700" title={gate.message}>
          Pending: {gate.message}
        </p>
      )}
      <button
        type="button"
        disabled={blocked || !canProcess}
        title={
          busyTitle ||
          (!gate.ok
            ? gate.message
            : unprocessedCount === 0
              ? "No unprocessed employees"
              : "Choose who to process")
        }
        onClick={() => onOpenAction?.("process")}
        className="inline-flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
      >
        <Play size={14} />
        Process ({unprocessedCount})
      </button>
      {hasAnyProcessed && (
        <>
          <button
            type="button"
            disabled={blocked || !canReprocess}
            title={busyTitle || "Choose processed employees to recalculate"}
            onClick={() => onOpenAction?.("reprocess")}
            className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs disabled:opacity-50 ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <RefreshCw size={14} />
            Reprocess ({processedCount})
          </button>
          <button
            type="button"
            disabled={blocked || !canUnprocess}
            title={busyTitle || "Choose processed employees to reverse"}
            onClick={() => onOpenAction?.("unprocess")}
            className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs disabled:opacity-50 ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <Undo2 size={14} />
            Unprocess ({processedCount})
          </button>
        </>
      )}
    </>
  );
}

export default function ProcessPayrollPanel({
  darkMode,
  run,
  payMonth,
  steps = [],
  unprocessedCount = 0,
  processedCount = 0,
  stepBusy,
  batchProgress = null,
  onOpenAction,
  processedRows = [],
  unprocessedEmployees = [],
  onViewBreakdown,
  onGoToStep,
  prevMonthBlocked = false,
  finalized = false,
  revealNegativeKey = 0,
  listTabSignal = null,
  serverBusy = null,
  processedIdSet = null,
  onHeaderExtras = null,
  onReloadProcessed = null,
}) {
  const gate = useMemo(() => computeProcessGate(steps), [steps]);
  const { hasScreenAccess, isAdmin } = usePermissions();
  /** Quick reports are nested grants under Process Payroll (pay_rpt_*), not a line Export. */
  const allowedReportKeys = useMemo(() => {
    if (isAdmin) return PAYROLL_QUICK_REPORT_ITEMS.map((r) => r.key);
    return PAYROLL_QUICK_REPORT_ITEMS.filter(
      (r) =>
        hasScreenAccess("payroll", r.screen, "view") ||
        hasScreenAccess("payroll", r.screen, "export")
    ).map((r) => r.key);
  }, [isAdmin, hasScreenAccess]);
  const exportableReportKeys = useMemo(() => {
    if (isAdmin) return new Set(PAYROLL_QUICK_REPORT_ITEMS.map((r) => r.key));
    return new Set(
      PAYROLL_QUICK_REPORT_ITEMS.filter((r) =>
        hasScreenAccess("payroll", r.screen, "export")
      ).map((r) => r.key)
    );
  }, [isAdmin, hasScreenAccess]);
  const canExport = exportableReportKeys.size > 0;
  const canEditTds =
    isAdmin ||
    hasScreenAccess("payroll", "pay_proc_process", "edit") ||
    hasScreenAccess("payroll", "pay_proc_process", "create");
  const [exporting, setExporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportPayload, setExportPayload] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [gateModal, setGateModal] = useState({ open: false, gate: null, reportLabel: "" });
  const [listTab, setListTab] = useState(() => (processedCount > 0 ? "processed" : "unprocessed"));
  const [tableRevealKey, setTableRevealKey] = useState(0);
  const [listSearch, setListSearch] = useState("");
  const [showNegativeSalary, setShowNegativeSalary] = useState(false);
  const [tdsReviewResult, setTdsReviewResult] = useState(null);
  const [tdsReviewLoading, setTdsReviewLoading] = useState(false);
  const [showAllTds, setShowAllTds] = useState(true);
  /** Inside Processed: payroll list vs inline TDS review (no popup). */
  const [processedPane, setProcessedPane] = useState("list"); // list | tds_review
  const [reconModalOpen, setReconModalOpen] = useState(false);
  const [reconMeta, setReconMeta] = useState({
    availableFinalisedMonths: [],
    defaultCompare: [],
    payMonth: "",
  });
  const [exportColumnMeta, setExportColumnMeta] = useState({
    fixedEarningComponentNames: [],
    benefitComponentNames: [],
    fixedComponentNames: [],
    paidFixedComponentNames: [],
    variableComponentNames: [],
    earningComponentNames: [],
    deductionComponentNames: [],
  });

  const negativeNetCount = useMemo(
    () => countNegativeNetFromRows(processedRows),
    [processedRows]
  );

  const [negativeModalOpen, setNegativeModalOpen] = useState(false);

  useEffect(() => {
    if (listTabSignal?.tab) setListTab(listTabSignal.tab);
  }, [listTabSignal]);

  // Land on Processed whenever count goes from empty → has processed; Unprocessed if none
  const prevProcessedCountRef = useRef(processedCount);
  useEffect(() => {
    const prev = prevProcessedCountRef.current;
    prevProcessedCountRef.current = processedCount;
    if (processedCount === 0) {
      setListTab("unprocessed");
      return;
    }
    if (prev === 0 && processedCount > 0) setListTab("processed");
  }, [processedCount]);

  const resolvedProcessedIdSet = useMemo(() => {
    if (processedIdSet && typeof processedIdSet.has === "function") return processedIdSet;
    const set = new Set();
    (processedRows || []).forEach((r) => {
      if (String(r.status || "") === "processed") {
        const id = Number(r.employeeId);
        if (Number.isFinite(id) && id > 0) set.add(id);
      }
    });
    return set;
  }, [processedIdSet, processedRows]);

  const loadTdsReview = useCallback(
    async ({ refresh = false, ledgerOnly = true } = {}) => {
      if (!run?.id) {
        toast.error("No payroll run loaded");
        return;
      }
      setTdsReviewLoading(true);
      try {
        // Default: ledger-only (Process already wrote PayrollEmployeeTds) — opens instantly.
        // refresh:true → full recompute + upsert (Refresh button).
        const useLedger = refresh ? false : ledgerOnly;
        const res = await payrollOpsService.validateStep(run.id, {
          stepKey: "tax_declarations",
          confirmScope: true,
          showAllTds: true,
          acknowledgeWarnings: true,
          ledgerOnly: useLedger,
          refresh: !!refresh,
        });
        setTdsReviewResult(res?.resultJson || res?.data?.resultJson || res?.data || res);
        setShowAllTds(true);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to load TDS review");
      } finally {
        setTdsReviewLoading(false);
      }
    },
    [run?.id]
  );

  const openTdsReview = async () => {
    setListTab("processed");
    setProcessedPane("tds_review");
    if (tdsReviewResult || tdsReviewLoading) return;
    await loadTdsReview({ ledgerOnly: true });
  };

  const backToProcessedList = () => {
    setProcessedPane("list");
  };

  useEffect(() => {
    if (listTab !== "processed") setProcessedPane("list");
  }, [listTab]);

  useEffect(() => {
    if (revealNegativeKey > 0) {
      setShowNegativeSalary(true);
      setListTab("processed");
      setProcessedPane("list");
    }
  }, [revealNegativeKey]);

  const showNegativeList = () => {
    setListTab("processed");
    setProcessedPane("list");
    setShowNegativeSalary(true);
    setTableRevealKey((k) => k + 1);
  };

  const companyId = useMemo(() => {
    try {
      const raw = localStorage.getItem("companyData");
      const data = raw ? JSON.parse(raw) : null;
      return data?.id || data?.companyId || null;
    } catch {
      return null;
    }
  }, []);

  const validationSteps = steps.filter(
    (s) =>
      !["process", "checklist", "company_checklist", "disbursement", "preview_validate", "finalise", "payslips"].includes(
        s.key
      )
  );
  const needsAttention = validationSteps.filter(
    (s) => s.blocking && !["passed", "acknowledged"].includes(s.status)
  );

  const errorByEmployeeId = useMemo(() => {
    const map = new Map();
    (processedRows || []).forEach((r) => {
      if (r.status === "error" && r.errorMessage) {
        map.set(Number(r.employeeId), r.errorMessage);
      }
    });
    return map;
  }, [processedRows]);

  const failedRows = useMemo(() => {
    return (processedRows || [])
      .filter((r) => r.status === "error")
      .map((r) => {
        const emp = (unprocessedEmployees || []).find(
          (e) => Number(e.id) === Number(r.employeeId)
        );
        const b = r.breakdownJson || {};
        return {
          employeeId: r.employeeId,
          empCode: emp?.empCode || b.empCode || r.empCode || r.employeeId,
          name: emp?.name || b.name || b.employeeName || r.name || "—",
          errorMessage: r.errorMessage || "Process failed",
        };
      });
  }, [processedRows, unprocessedEmployees]);

  const unprocessedRows = useMemo(() => {
    return (unprocessedEmployees || []).map((e) => ({
      employeeId: e.id,
      empCode: e.empCode || e.id,
      name: e.name || "—",
      errorMessage: errorByEmployeeId.get(Number(e.id)) || null,
    }));
  }, [unprocessedEmployees, errorByEmployeeId]);

  const failedCount = failedRows.length;

  const tdsSummary = useMemo(() => {
    let totalTds = 0;
    let totalComputed = 0;
    let overrideCount = 0;
    let withTds = 0;
    (processedRows || []).forEach((r) => {
      if (r.status !== "processed" || !r.breakdownJson) return;
      const b = r.breakdownJson || {};
      const applied = Number(b.tdsAmount ?? b.tds ?? 0) || 0;
      const computed = Number(b.tdsMeta?.computedTds ?? applied) || 0;
      totalTds += applied;
      totalComputed += computed;
      if (applied > 0) withTds += 1;
      if (b.tdsMeta?.isOverridden || b.tdsMeta?.override) overrideCount += 1;
    });
    return {
      totalTds: Math.round(totalTds * 100) / 100,
      totalComputed: Math.round(totalComputed * 100) / 100,
      delta: Math.round((totalTds - totalComputed) * 100) / 100,
      overrideCount,
      withTds,
      processed: (processedRows || []).filter((r) => r.status === "processed" && r.breakdownJson)
        .length,
    };
  }, [processedRows]);

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";

  const showProcessRing =
    batchProgress &&
    (batchProgress.action === "process" || batchProgress.action === "reprocess");
  const showUnprocessRing = batchProgress && batchProgress.action === "unprocess";
  const isBatchBusy = !!(showProcessRing || showUnprocessRing);
  const reportSelectRef = useRef(null);

  // Push Unprocessed/Processed + Quick Reports into the Process Payroll title row.
  useEffect(() => {
    if (typeof onHeaderExtras !== "function") return undefined;
    const tabBtn = (tab, label, count, activeCls, idleCls = border) => (
      <button
        key={tab}
        type="button"
        onClick={() => setListTab(tab)}
        disabled={isBatchBusy}
        className={`rounded border px-2 py-1 text-[11px] ${
          listTab === tab ? activeCls : idleCls
        } ${isBatchBusy ? "cursor-not-allowed opacity-50" : ""}`}
      >
        {label} <strong className="tabular-nums">{count}</strong>
        {tab === "processed" && negativeNetCount > 0 ? (
          <span className="ml-1 tabular-nums text-[hsl(var(--ds-danger))]">
            · {negativeNetCount} neg
          </span>
        ) : null}
      </button>
    );
    onHeaderExtras({
      left: (
        <div className="flex flex-wrap items-center gap-1.5">
          {tabBtn(
            "unprocessed",
            "Unprocessed",
            unprocessedCount,
            "border-emerald-600 bg-emerald-50 font-semibold text-emerald-900"
          )}
          {tabBtn(
            "processed",
            "Processed",
            processedCount,
            "border-emerald-600 bg-emerald-50 font-semibold text-emerald-900"
          )}
          {failedCount > 0
            ? tabBtn(
                "failed",
                "Failed",
                failedCount,
                "border-red-500 bg-red-50 font-semibold text-red-800",
                "border-red-300 bg-red-50 text-red-800"
              )
            : null}
          {!gate.ok ? (
            <span className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
              Gate: {gate.message}
            </span>
          ) : null}
        </div>
      ),
      right: canExport ? (
        <PayrollReportsMenu
          darkMode={darkMode}
          disabled={stepBusy || isBatchBusy}
          exporting={exporting}
          allowedKeys={allowedReportKeys}
          onSelect={(key) => reportSelectRef.current?.(key)}
        />
      ) : null,
    });
    return () => onHeaderExtras(null);
  }, [
    onHeaderExtras,
    listTab,
    unprocessedCount,
    processedCount,
    failedCount,
    negativeNetCount,
    gate.ok,
    gate.message,
    canExport,
    allowedReportKeys,
    exportableReportKeys,
    darkMode,
    stepBusy,
    isBatchBusy,
    exporting,
    border,
  ]);

  // Drop stale review while Process/Unprocess runs; prefetch ledger after done
  const prefetchDoneRef = useRef(null);
  useEffect(() => {
    if (isBatchBusy) {
      setTdsReviewResult(null);
      prefetchDoneRef.current = null;
      setProcessedPane("list");
      return;
    }
    if (!run?.id || processedCount <= 0) return;
    const key = `${run.id}:${processedCount}`;
    if (prefetchDoneRef.current === key) return;
    prefetchDoneRef.current = key;
    loadTdsReview({ ledgerOnly: true }).catch(() => {
      prefetchDoneRef.current = null;
    });
  }, [isBatchBusy, run?.id, processedCount, loadTdsReview]);

  const filterColumnMeta = (data) => ({
    fixedEarningComponentNames: data?.fixedEarningComponentNames || [],
    benefitComponentNames: data?.benefitComponentNames || [],
    fixedComponentNames: data?.fixedComponentNames || [],
    paidFixedComponentNames: data?.paidFixedComponentNames || [],
    variableComponentNames: data?.variableComponentNames || [],
    earningComponentNames: (data?.earningComponentNames || []).filter(
      (n) => !/^lop\s*deduction$/i.test(n)
    ),
    deductionComponentNames: (data?.deductionComponentNames || []).filter(
      (n) => !/^lop\s*deduction$/i.test(n)
    ),
  });

  const showExportGate = (err, reportKey) => {
    const body = err?.response?.data;
    const requireAll = reportKey === "projection" || body?.gate?.requireAll === true;
    const gateInfo = body?.gate || {
      requireAll,
      processedCount,
      scopeCount: processedCount + unprocessedCount,
      unprocessedCount,
      message:
        body?.message ||
        (requireAll
          ? "Please process all employees' payroll before exporting this report."
          : "Process at least one employee before exporting this report."),
    };
    setGateModal({
      open: true,
      gate: { ...gateInfo, requireAll: gateInfo.requireAll ?? requireAll },
      reportLabel: REPORT_LABELS[reportKey] || "this report",
    });
  };

  const isExportGateError = (err) => {
    const status = err?.response?.status;
    const gateInfo = err?.response?.data?.gate;
    const msg = String(err?.response?.data?.message || err?.message || "").toLowerCase();
    return (
      status === 400 &&
      (gateInfo?.ok === false ||
        msg.includes("process all") ||
        msg.includes("at least one") ||
        (typeof gateInfo?.unprocessedCount === "number" && gateInfo.processedCount < 1))
    );
  };

  /** Per 24 Jul instruction: process-all only for Projection; other reports need ≥1 processed. */
  const hardClientGate = (reportKey) => {
    if (!run?.id) return false;
    const scopeTotal = processedCount + unprocessedCount;
    const requireAll = reportKey === "projection";

    if (requireAll) {
      if (unprocessedCount > 0 || processedCount < 1) {
        setGateModal({
          open: true,
          gate: {
            requireAll: true,
            processedCount,
            scopeCount: scopeTotal,
            unprocessedCount: unprocessedCount || scopeTotal,
            message: `Please process all employees' payroll before exporting this report (${processedCount}/${scopeTotal} processed).`,
          },
          reportLabel: REPORT_LABELS[reportKey] || "this report",
        });
        return false;
      }
      return true;
    }

    if (processedCount < 1) {
      setGateModal({
        open: true,
        gate: {
          requireAll: false,
          processedCount: 0,
          scopeCount: scopeTotal,
          unprocessedCount: scopeTotal,
          message: `Process at least one employee before exporting this report (0/${scopeTotal} processed).`,
        },
        reportLabel: REPORT_LABELS[reportKey] || "this report",
      });
      return false;
    }
    return true;
  };

  const handleReportSelect = async (reportKey) => {
    if (!run?.id || stepBusy) return;
    if (!allowedReportKeys.includes(reportKey)) {
      toast.error("No permission for this report. Grant View under Payroll → Process → Quick Reports.");
      return;
    }
    if (!exportableReportKeys.has(reportKey)) {
      toast.error("No permission to export this report. Grant Export under Payroll → Process → Quick Reports.");
      return;
    }

    if (reportKey === "unpaid") {
      setExporting(true);
      try {
        const res = await payrollOpsService.getUnpaidSalaryReport({
          toMonth: payMonth || run.payMonth,
        });
        const data = res?.data || res;
        if (!data?.rows?.length) {
          toast.error("No open unpaid salary / F&F to export");
          return;
        }
        const filename = await exportUnpaidSalaryExcel({
          rows: data.rows,
          monthSummary: data.monthSummary || [],
          accumulation: data.accumulation || [],
          toMonth: payMonth || run.payMonth,
        });
        toast.success(`Exported ${filename}`);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Export failed");
      } finally {
        setExporting(false);
      }
      return;
    }

    if (reportKey === "le_away") {
      setExporting(true);
      try {
        const res = await payrollPolicyService.listAwayLeaveEncashmentRegister({
          payMonth: payMonth || run.payMonth,
        });
        const data = res?.data || res;
        if (!data?.rows?.length) {
          toast.error("No away-from-payroll leave encashment for this month");
          return;
        }
        const filename = await exportLeaveEncashmentAwayExcel(data, {
          payMonth: payMonth || run.payMonth,
        });
        toast.success(`Exported ${filename}`);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Export failed");
      } finally {
        setExporting(false);
      }
      return;
    }

    if (!hardClientGate(reportKey)) return;

    if (reportKey === "projection") {
      setExporting(true);
      try {
        const prefs = loadSalaryRegisterExportPrefs(companyId);
        const res = await payrollOpsService.getPayrollProjectionReport(
          run.id,
          payMonth || run.payMonth
        );
        const data = res?.data || res;
        const filename = await exportPayrollProjectionExcel(data, {
          columnOrder: {
            fixedEarningOrder: prefs?.fixedEarningOrder,
            benefitOrder: prefs?.benefitOrder,
            paidFixedOrder: prefs?.paidFixedOrder,
            variableOrder: prefs?.variableOrder,
            deductionOrder: prefs?.deductionOrder,
            fixedOrder: prefs?.fixedOrder,
            earningOrder: prefs?.earningOrder,
          },
        });
        toast.success(`Exported ${filename}`);
      } catch (err) {
        if (isExportGateError(err)) showExportGate(err, reportKey);
        else toast.error(err?.response?.data?.message || err?.message || "Export failed");
      } finally {
        setExporting(false);
      }
      return;
    }

    if (reportKey === "reconciliation") {
      setExporting(true);
      try {
        const res = await payrollOpsService.getPayrollReconciliationMeta(
          run.id,
          payMonth || run.payMonth
        );
        const data = res?.data || res;
        setReconMeta({
          availableFinalisedMonths: data?.availableFinalisedMonths || [],
          defaultCompare: data?.defaultCompare || [],
          payMonth: data?.payMonth || payMonth || run.payMonth,
        });
        setReconModalOpen(true);
      } catch (err) {
        if (isExportGateError(err)) showExportGate(err, reportKey);
        else toast.error(err?.response?.data?.message || err?.message || "Could not load months");
      } finally {
        setExporting(false);
      }
      return;
    }

    setExporting(true);
    setActiveReport(reportKey);
    try {
      const prefs = loadSalaryRegisterExportPrefs(companyId);
      const fetcher =
        reportKey === "journal"
          ? payrollOpsService.getAccountingJournalReport
          : payrollOpsService.getPayrollRegisterReport;
      const res = await fetcher(run.id, payMonth || run.payMonth, {
        jvGroupBy: prefs?.jvGroupBy || "all",
      });
      const data = res?.data || res;
      setExportPayload(data);
      setExportColumnMeta(filterColumnMeta(data));
      setExportModalOpen(true);
    } catch (err) {
      setActiveReport(null);
      if (isExportGateError(err)) showExportGate(err, reportKey);
      else toast.error(err?.response?.data?.message || err?.message || "Could not load export data");
    } finally {
      setExporting(false);
    }
  };
  reportSelectRef.current = handleReportSelect;

  const handleOptionsExport = async (opts = {}) => {
    if (!run?.id || !activeReport) return;
    setExporting(true);
    try {
      let data = exportPayload;
      const needRefetch =
        activeReport === "journal" &&
        (!data?.rows?.length ||
          String(data?.jvGroupBy || "all") !== String(opts.jvGroupBy || "all"));
      if (needRefetch || !data?.rows?.length) {
        const fetcher =
          activeReport === "journal"
            ? payrollOpsService.getAccountingJournalReport
            : payrollOpsService.getPayrollRegisterReport;
        const res = await fetcher(run.id, payMonth || run.payMonth, {
          jvGroupBy: opts.jvGroupBy || "all",
        });
        data = res?.data || res;
        setExportPayload(data);
      }
      if (!data?.rows?.length) {
        toast.error("No processed payroll data found to export");
        return;
      }
      const filename =
        activeReport === "journal"
          ? await exportAccountingJournalExcel(data, { columnOrder: opts.columnOrder })
          : await exportPayrollRegisterExcel(data, { columnOrder: opts.columnOrder });
      toast.success(`Exported ${filename}`);
      setExportModalOpen(false);
      setExportPayload(null);
      setActiveReport(null);
    } catch (err) {
      if (isExportGateError(err)) showExportGate(err, activeReport);
      else toast.error(err?.response?.data?.message || err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleReconExport = async (comparePayMonths = []) => {
    if (!run?.id) return;
    setExporting(true);
    try {
      const res = await payrollOpsService.getPayrollReconciliationReport(
        run.id,
        payMonth || run.payMonth,
        comparePayMonths
      );
      const data = res?.data || res;
      const filename = await exportPayrollReconciliationExcel(data);
      toast.success(`Exported ${filename}`);
      setReconModalOpen(false);
    } catch (err) {
      if (isExportGateError(err)) showExportGate(err, "reconciliation");
      else toast.error(err?.response?.data?.message || err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {needsAttention.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {needsAttention.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => onGoToStep?.(s.key)}
              className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[10px] font-medium text-amber-900"
            >
              {s.label}: {s.status || "pending"}
            </button>
          ))}
        </div>
      )}

      {prevMonthBlocked && (
        <p
          className={`rounded-lg border px-3 py-2 text-xs ${
            darkMode
              ? "border-amber-700/50 bg-amber-950/40 text-amber-100"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}
        >
          Previous month payroll is not finalised. Finalise the previous month before Process /
          Unprocess.
        </p>
      )}

      {finalized && (
        <p
          className={`rounded-lg border px-3 py-2 text-xs ${
            darkMode
              ? "border-emerald-800/50 bg-emerald-950/30 text-emerald-100"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
        >
          This month is finalised. Unlock from the Finalise step before processing changes.
        </p>
      )}

      {serverBusy && <ServerBusyCard busy={serverBusy} darkMode={darkMode} />}

      {negativeNetCount > 0 && (
        <div
          className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${
            darkMode
              ? "border-red-800/50 bg-red-950/30 text-red-100"
              : "border-red-200 bg-red-50 text-red-900"
          }`}
        >
          <span>
            <strong className="tabular-nums">{negativeNetCount}</strong> employee
            {negativeNetCount === 1 ? "" : "s"} with negative net pay this month.
          </span>
          <button
            type="button"
            onClick={showNegativeList}
            className={`shrink-0 rounded border px-2 py-1 font-semibold ${
              darkMode
                ? "border-red-700 bg-red-900/40 hover:bg-red-900/60"
                : "border-red-300 bg-white hover:bg-red-100"
            }`}
          >
            View list
          </button>
        </div>
      )}

      <SalaryRegisterExportModal
        darkMode={darkMode}
        open={exportModalOpen}
        onClose={() => {
          setExportModalOpen(false);
          setExportPayload(null);
          setActiveReport(null);
        }}
        onConfirm={handleOptionsExport}
        exporting={exporting}
        companyId={companyId}
        fixedEarningComponentNames={exportColumnMeta.fixedEarningComponentNames}
        benefitComponentNames={exportColumnMeta.benefitComponentNames}
        fixedComponentNames={exportColumnMeta.fixedComponentNames}
        paidFixedComponentNames={exportColumnMeta.paidFixedComponentNames}
        variableComponentNames={exportColumnMeta.variableComponentNames}
        earningComponentNames={exportColumnMeta.earningComponentNames}
        deductionComponentNames={exportColumnMeta.deductionComponentNames}
        title={
          activeReport === "journal"
            ? "Accounting Journal export options"
            : "Payroll Register export options"
        }
        subtitle={
          activeReport === "journal"
            ? "JV grouping by master · save component column order for Sheet 2"
            : "Save component column order for next time"
        }
        showJvOptions={activeReport === "journal"}
      />

      <PayrollReconciliationExportModal
        darkMode={darkMode}
        open={reconModalOpen}
        onClose={() => setReconModalOpen(false)}
        onConfirm={handleReconExport}
        exporting={exporting}
        currentPayMonth={reconMeta.payMonth || payMonth || run?.payMonth}
        availableFinalisedMonths={reconMeta.availableFinalisedMonths}
        defaultCompare={reconMeta.defaultCompare}
      />

      <PayrollAllProcessedGateModal
        darkMode={darkMode}
        open={gateModal.open}
        onClose={() => setGateModal({ open: false, gate: null, reportLabel: "" })}
        reportLabel={gateModal.reportLabel}
        gate={gateModal.gate}
      />

      <NegativeNetPayModal
        open={negativeModalOpen}
        rows={processedRows}
        onClose={() => setNegativeModalOpen(false)}
        onShowInList={showNegativeList}
        onViewBreakdown={onViewBreakdown}
      />

      {isBatchBusy ? (
        <div
          className={`flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden rounded-lg border ${border}`}
        >
          {showProcessRing ? (
            <div className="w-full max-w-lg p-6">
              <ProcessPhaseProgress batchProgress={batchProgress} darkMode={darkMode} />
            </div>
          ) : null}
          {showUnprocessRing ? (
            <div className="flex justify-center p-8">
              <ProgressRing
                darkMode={darkMode}
                done={batchProgress.done}
                total={batchProgress.total}
                label="Unprocessing"
                color="#dc2626"
              />
            </div>
          ) : null}
        </div>
      ) : (
      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row lg:items-stretch">
        {/* Section 1 — payroll list OR inline TDS review (same Processed area) */}
        <div
          className={`flex min-h-[320px] min-w-0 flex-1 flex-col overflow-hidden rounded-lg border lg:min-h-0 ${border}`}
        >
          {listTab === "processed" ? (
            <>
              <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-[hsl(var(--ds-border))] px-2 py-1.5">
                <button
                  type="button"
                  onClick={backToProcessedList}
                  className={`rounded px-2.5 py-1 text-[11px] font-medium ${
                    processedPane === "list"
                      ? "bg-emerald-50 font-semibold text-emerald-900"
                      : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  Payroll list
                </button>
                <button
                  type="button"
                  onClick={openTdsReview}
                  className={`rounded px-2.5 py-1 text-[11px] font-medium ${
                    processedPane === "tds_review"
                      ? "bg-emerald-50 font-semibold text-emerald-900"
                      : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  TDS Review / Audit
                </button>
                {processedPane === "list" ? (
                  <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
                    <div className="w-72 min-w-[16rem] sm:w-80">
                      <EmpCodeNameSearchBar
                        value={listSearch}
                        onChange={setListSearch}
                        darkMode={darkMode}
                        className="!flex-none w-full"
                      />
                    </div>
                    <label
                      className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded border px-2 py-1 text-[11px] ${
                        showNegativeSalary || negativeNetCount > 0
                          ? darkMode
                            ? "border-red-800/60 bg-red-950/40 text-red-100"
                            : "border-red-300 bg-red-50 text-red-900"
                          : darkMode
                            ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
                            : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                      }`}
                      title="Show only processed employees whose net pay is less than zero"
                    >
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-[hsl(var(--ds-danger))]"
                        checked={!!showNegativeSalary}
                        onChange={(e) => setShowNegativeSalary(e.target.checked)}
                      />
                      <span>
                        Show Negative Salary{" "}
                        <strong className="tabular-nums">({negativeNetCount})</strong>
                      </span>
                    </label>
                  </div>
                ) : (
                  <div className="ml-auto flex items-center gap-2">
                    <span className="rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                      FY{" "}
                      {(() => {
                        const s = String(payMonth || run?.payMonth || "").slice(0, 7);
                        const m = s.match(/^(\d{4})-(\d{2})$/);
                        if (!m) return "—";
                        const y = Number(m[1]);
                        const month = Number(m[2]);
                        return month >= 4
                          ? `${y}-${String((y + 1) % 100).padStart(2, "0")}`
                          : `${y - 1}-${String(y % 100).padStart(2, "0")}`;
                      })()}
                    </span>
                    <button
                      type="button"
                      onClick={() => loadTdsReview({ refresh: true })}
                      disabled={tdsReviewLoading}
                      className="rounded border border-[hsl(var(--ds-border))] p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] disabled:opacity-50"
                      title="Refresh TDS review"
                    >
                      <RefreshCw size={14} className={tdsReviewLoading ? "animate-spin" : ""} />
                    </button>
                  </div>
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">
                {processedPane === "tds_review" ? (
                  <div className="flex h-full min-h-0 flex-col overflow-hidden p-2">
                    <PayrollDeclarationsTdsPanel
                      result={tdsReviewResult}
                      payMonth={payMonth || run?.payMonth}
                      darkMode={darkMode}
                      loading={tdsReviewLoading}
                      showAllTds={showAllTds}
                      processedIdSet={resolvedProcessedIdSet}
                      onToggleShowAll={(checked) => setShowAllTds(checked)}
                      onRefresh={() => loadTdsReview({ refresh: true })}
                      onRunCheck={() => loadTdsReview({ ledgerOnly: true })}
                    />
                  </div>
                ) : (
                  <ProcessedResultsTable
                    rows={processedRows}
                    darkMode={darkMode}
                    onViewBreakdown={onViewBreakdown}
                    revealNegativeKey={tableRevealKey}
                    payMonth={payMonth || run?.payMonth}
                    finalized={!!finalized}
                    canEditTds={canEditTds}
                    onTdsSaved={() => onReloadProcessed?.()}
                    hideToolbar
                    search={listSearch}
                    onSearchChange={setListSearch}
                    showNegativeSalary={showNegativeSalary}
                    onShowNegativeChange={setShowNegativeSalary}
                  />
                )}
              </div>
            </>
          ) : listTab === "failed" ? (
            <UnprocessedOrFailedTable
              rows={failedRows}
              darkMode={darkMode}
              emptyLabel="No process failures for this run."
            />
          ) : (
            <UnprocessedOrFailedTable
              rows={unprocessedRows}
              darkMode={darkMode}
              emptyLabel="All in-scope employees are processed."
            />
          )}
        </div>
        {/* Section 2 — TDS summary only on Processed · payroll list (not during review pane) */}
        {listTab === "processed" && processedCount > 0 && processedPane === "list" ? (
          <div className="flex w-full shrink-0 flex-col lg:w-[280px] xl:w-[300px]">
            <TdsProcessSummaryCard
              tdsSummary={tdsSummary}
              darkMode={darkMode}
              onOpenTdsReview={openTdsReview}
            />
          </div>
        ) : null}
      </div>
      )}
    </div>
  );
}

export { computeProcessGate, ProgressRing };
